import {
  HEALTHY_CONVERSION_RATE,
  STAGE_LABEL,
  capitalise,
  computeLadder,
  countLost,
  countsTowardLifetime,
  formatAddress,
  formatMoney,
  formatPhoneInput,
  isLost,
  lifetimeValue,
  primaryPhone,
  US_STATE_OPTIONS,
  type Contact,
  type Job,
  type LadderTotals,
  type LifetimeValue,
} from "@roof-hawk/core";
import { useCallback, useMemo, useState } from "react";

import { AddressInput } from "../../components/AddressInput";
import { ErrorNote, Row, Stub } from "../../components/ui";
import { WorkingOnNotice } from "../../components/WorkingOnNotice";
import { t } from "../../i18n";
import { CallButton } from "../phone/CallButton";
import { fmtDateTime, streetViewUrl } from "../../lib/format";
import { navigate, useAsync } from "../../lib/hooks";
import {
  fetchContact,
  fetchContactActivity,
  fetchContactJobs,
  fetchLadderInputs,
  updateRow,
} from "../../lib/queries";
import { supabase } from "../../lib/supabase";
import { useWorkingOn } from "../../lib/useWorkingOn";
import { useSession } from "../../session";
import type { AppData } from "../shell/Shell";
import { ContactFlags } from "./ContactFlags";

/**
 * The customer page: shared contact details, every job they have, and activity
 * rolled up across all of them (ADR 0005).
 */
export function ContactDetail({
  contactId,
  data,
  onNewJob,
}: {
  contactId: string;
  data: AppData;
  onNewJob: (contact: Contact) => void;
}) {
  const [saveError, setSaveError] = useState<string | null>(null);
  // Collapsed by default — it is the longest thing on the page and the least
  // often the reason someone opened it.
  const [showActivity, setShowActivity] = useState(false);
  const { profileId, displayName } = useSession();
  // The customer file counts as being on the customer (ADR 0268).
  const othersHere = useWorkingOn(
    contactId,
    { profileId, name: displayName },
    null,
  );

  const contactQuery = useAsync(() => fetchContact(contactId), [contactId]);
  const jobsQuery = useAsync(() => fetchContactJobs(contactId), [contactId]);
  const ladderQuery = useAsync(
    async () =>
      fetchLadderInputs(
        ((await fetchContactJobs(contactId)) ?? [])
          .filter(countsTowardLifetime)
          .map((job) => job.id),
      ),
    [contactId],
  );
  const activityQuery = useAsync(
    async () =>
      fetchContactActivity(
        contactId,
        (await fetchContactJobs(contactId)) ?? [],
      ),
    [contactId],
  );

  const contact = contactQuery.data;

  const save = useCallback(
    async (patch: Record<string, unknown>) => {
      if (!contact) return;
      try {
        await updateRow("contacts", contact.id, patch);
        setSaveError(null);
        // Refetched, not patched: full_name is rebuilt from first_name and
        // last_name by a database trigger, so the heading on this page is the
        // database's answer rather than ours.
        await Promise.all([contactQuery.reload(), data.reload()]);
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : String(e));
      }
    },
    [contact, contactQuery, data],
  );

  const jobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);

  /**
   * What this contact has been worth: revenue, what we kept, and the margin.
   *
   * Every figure comes from `computeLadder`, the same function the estimate
   * card uses, so the contact page and the job can never disagree. Only
   * approved work that has not been written off counts.
   *
   * A job can carry more than one live estimate where the customer bought two
   * options rather than choosing between them, so this is a ladder per active
   * estimate and they add up.
   */
  const value = useMemo(() => {
    if (!ladderQuery.data) return null;
    const totals: LadderTotals[] = jobs
      .filter(countsTowardLifetime)
      .flatMap((job) =>
        (ladderQuery.data!.get(job.id) ?? []).map((input) =>
          computeLadder({ ...input, jobType: job.job_type, place: job }),
        ),
      );
    return lifetimeValue(totals, {
      approved: jobs.filter(countsTowardLifetime).length,
      total: jobs.length,
    });
  }, [jobs, ladderQuery.data]);

  if (contactQuery.loading) return <div className="list" />;
  if (!contact) {
    return (
      <div className="list">
        <div className="empty">
          <b>{t("Contact not found")}</b>
          {t("They may have been removed.")}
        </div>
      </div>
    );
  }

  const lostCount = countLost(jobs);
  const latestActivity = (activityQuery.data ?? [])[0]?.when ?? null;

  return (
    <>
      <div className="dhead">
        <div className="thumb">
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <h1>
            {contact.full_name ?? t("Contact")}
            <WorkingOnNotice
              contactId={contactId}
              others={othersHere}
              customerName={contact.full_name ?? t("this customer")}
            />
          </h1>
          <div className="addr">{formatAddress(contact)}</div>
          {/* Call from here (ADR 0310). The rule follows the newest job's
              source; a customer with no job is called from the rep's phone. */}
          <div className="dhead-call">
            <CallButton
              number={primaryPhone(contact)}
              leadSource={jobs[0]?.lead_source ?? null}
              jobId={jobs[0]?.id ?? null}
              contactId={contactId}
            />
          </div>
        </div>
      </div>

      <ContactFlags contact={contact} onSave={save} lostCount={lostCount} />
      <LifetimeBar value={value} />

      <ErrorNote error={saveError} />

      <div className="cols">
        <div className="block">
          <h2>{t("Contact Info")}</h2>
          {/* The parts, not the joined name. `full_name` is rebuilt from these
              by a database trigger, so typing into it would be overwritten the
              moment either part changed. */}
          <Row label={t("First name")}>
            <TextField
              value={contact.first_name}
              onSave={(v) => void save({ first_name: v })}
            />
          </Row>
          <Row label={t("Last name")}>
            <TextField
              value={contact.last_name}
              onSave={(v) => void save({ last_name: v })}
            />
          </Row>
          <Row label={t("Company")}>
            <TextField
              value={contact.company}
              onSave={(v) => void save({ company: v })}
            />
          </Row>
          {/* The cell is first here for the same reason it is first
              everywhere: it is the number that gets answered. */}
          <Row label={t("Cell")}>
            <TextField
              value={contact.cell}
              width={160}
              format={formatPhoneInput}
              onSave={(v) => void save({ cell: v })}
            />
          </Row>
          {/* Two boxes, because a second number is no use to the rep holding
              it unless they know who picks it up. */}
          <Row label={t("Alternate phone")}>
            <TextField
              value={contact.phone}
              width={160}
              format={formatPhoneInput}
              onSave={(v) => void save({ phone: v })}
            />{" "}
            <TextField
              value={contact.phone_label}
              width={150}
              placeholder={t("Whose? e.g. spouse")}
              onSave={(v) => void save({ phone_label: v })}
            />
          </Row>
          {(contact.extra_phones?.length ?? 0) > 0 && (
            <Row label={t("Other numbers")}>
              {contact.extra_phones!.map((number, index) => (
                <span key={`${number}-${index}`}>
                  {index > 0 ? ", " : ""}
                  {formatPhoneInput(number)}
                </span>
              ))}
            </Row>
          )}
          <Row label={t("Email")}>
            <TextField
              value={contact.email}
              onSave={(v) => void save({ email: v })}
            />
          </Row>
          <Row label={t("Mailing Address")}>
            <AddressInput
              value={contact.address ?? ""}
              style={{ maxWidth: 165 }}
              onChange={(v) => contactQuery.setData({ ...contact, address: v })}
              onPick={(pick) =>
                void save({
                  address: pick.address,
                  city: pick.city,
                  state: pick.state,
                  zip: pick.zip,
                  lat: pick.lat,
                  lng: pick.lng,
                })
              }
            />{" "}
            <a
              href={streetViewUrl(contact)}
              target="_blank"
              rel="noreferrer"
              className="na-hint"
            >
              {t("Street View ↗")}
            </a>
          </Row>
          <Row label={t("City")}>
            <TextField
              value={contact.city}
              width={150}
              onSave={(v) => void save({ city: v })}
            />
          </Row>
          <Row label={t("State")}>
            <select
              value={contact.state ?? ""}
              onChange={(e) => void save({ state: e.target.value || null })}
            >
              <option value="">—</option>
              {US_STATE_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </select>
          </Row>
          <Row label={t("Zip")}>
            <TextField
              value={contact.zip}
              width={90}
              onSave={(v) => void save({ zip: v })}
            />
          </Row>
          <Row label={t("Contact Since")}>
            {fmtDateTime(contact.created_at)}
          </Row>

          {/* A duplicate lead entered twice is two customer rows, and the empty
              one should go. Only offered once there are no jobs left — the
              database refuses otherwise rather than cascading work away. */}
          {jobs.length === 0 && (
            <DeleteContactButton contact={contact} onDeleted={data.reload} />
          )}
        </div>

        <div className="block">
          <h2>
            {t("Jobs")} <span className="na-hint">({jobs.length})</span>
          </h2>
          <button className="reassign" onClick={() => onNewJob(contact)}>
            {t("+ New job for this contact")}
          </button>
          {jobs.length === 0 ? (
            <Stub
              title={t("No jobs yet")}
              message={t("Create the first one above.")}
            />
          ) : (
            jobs.map((job) => (
              <div
                key={job.id}
                className="pcard"
                onClick={() => navigate(`job=${job.id}`)}
              >
                <div className="cn">
                  #{job.job_number} ·{" "}
                  {job.address ?? formatAddress(contact) ?? t("No address")}
                </div>
                <div className="ca">
                  {t("Created {when}", { when: fmtDateTime(job.created_at) })}
                  {job.rep?.full_name ? ` · ${job.rep.full_name}` : ""}
                </div>
                <span className={`pill ${job.bid_type}`}>
                  {t(capitalise(job.bid_type))}
                </span>{" "}
                <span className={`pill${isLost(job) ? " lostpill" : ""}`}>
                  {t(STAGE_LABEL[job.stage] ?? job.stage)}
                </span>
                {isLost(job) && job.lost_reason && (
                  <div className="na-hint">{job.lost_reason}</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="block" style={{ marginTop: 16 }}>
        {/* Collapsed by default: it is the longest thing on the page and the
            least often the reason someone opened it. */}
        <h2
          className="collapsible"
          onClick={() => setShowActivity((open) => !open)}
          role="button"
          tabIndex={0}
        >
          <span className={`caret${showActivity ? " open" : ""}`}>▸</span>{" "}
          {t("Activity — All Jobs")}{" "}
          <span className="na-hint">
            ({(activityQuery.data ?? []).length}
            {latestActivity
              ? ` · ${t("latest {when}", { when: fmtDateTime(latestActivity) })}`
              : ""}
            )
          </span>
        </h2>
        {showActivity &&
          ((activityQuery.data ?? []).length === 0 ? (
            <Stub
              title={t("No activity yet")}
              message={t(
                "Notes and status changes from every job for this contact will collect here.",
              )}
            />
          ) : (
            (activityQuery.data ?? []).map((row, i) => (
              <div className="note" key={`${row.when}-${i}`}>
                <div className={`ntype ${row.kind}`}>
                  {t(KIND_LABEL[row.kind] ?? "NOTE")}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="nmeta">
                    {row.jobNumber && (
                      <>
                        <span className="jn">#{row.jobNumber}</span>
                        <span>·</span>
                      </>
                    )}
                    <span>{row.who ?? t("Someone")}</span>
                    <span>·</span>
                    <span>{fmtDateTime(row.when)}</span>
                    {jobLabel(row.jobNumber, jobs) && (
                      <>
                        <span>·</span>
                        <span>{jobLabel(row.jobNumber, jobs)}</span>
                      </>
                    )}
                  </div>
                  <div>
                    {row.kind === "stage"
                      ? t("Status → {stage}", {
                          stage: t(
                            STAGE_LABEL[
                              row.text.replace("Status → ", "") as never
                            ] ?? row.text.replace("Status → ", ""),
                          ),
                        })
                      : row.text}
                  </div>
                </div>
              </div>
            ))
          ))}
      </div>
    </>
  );
}

/** An input that saves on change, the app-wide convention. */
export function TextField({
  value,
  onSave,
  width,
  type,
  format,
  placeholder,
}: {
  value: string | null | undefined;
  onSave: (value: string | null) => void;
  width?: number;
  type?: string;
  /** Rewrites the text on every keystroke — phone numbers use it for dashes. */
  format?: (value: string) => string;
  placeholder?: string;
}) {
  const [local, setLocal] = useState(value ?? "");
  const [dirty, setDirty] = useState(false);

  // Follow the row when it changes underneath us, unless mid-edit.
  if (!dirty && (value ?? "") !== local) {
    setLocal(value ?? "");
  }

  return (
    <input
      type={type}
      value={local}
      placeholder={placeholder}
      style={width ? { maxWidth: width } : undefined}
      onChange={(e) => {
        setLocal(format ? format(e.target.value) : e.target.value);
        setDirty(true);
      }}
      onBlur={() => {
        if (!dirty) return;
        setDirty(false);
        onSave(local === "" ? null : local);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

const KIND_LABEL: Record<string, string> = {
  done: "STEP",
  stage: "STATUS",
  note: "NOTE",
};

/** Which job a row belongs to, in words — "#4014911, Lost" reads at a glance. */
function jobLabel(
  jobNumber: number | undefined,
  jobs: readonly Job[],
): string | null {
  if (!jobNumber) return null;
  const job = jobs.find((j) => j.job_number === jobNumber);
  if (!job) return null;
  return isLost(job) ? t("Lost") : t(STAGE_LABEL[job.stage] ?? job.stage);
}

/**
 * What this contact has been worth, across every approved job.
 *
 * Three figures rather than one: a contact who spends a lot at a thin margin
 * and one who spends less at a healthy margin look identical under a single
 * number, and they are not the same customer.
 */
function LifetimeBar({ value }: { value: LifetimeValue | null }) {
  if (!value) return null;

  if (value.totalJobs === 0) {
    return (
      <div className="ltv">
        <span className="na-hint">
          {t("No jobs yet — figures appear once this contact has one.")}
        </span>
      </div>
    );
  }

  return (
    <div className="ltv">
      <div className="ltv-fig">
        <span className="k">{t("Total revenue")}</span>
        <b>{formatMoney(value.revenue)}</b>
      </div>
      <div className="ltv-fig">
        <span className="k">{t("Gross profit")}</span>
        <b className={value.grossProfit < 0 ? "bad" : undefined}>
          {formatMoney(value.grossProfit)}
        </b>
      </div>
      <div className="ltv-fig">
        <span className="k">{t("Margin")}</span>
        <b className={value.margin < 0 ? "bad" : undefined}>
          {(value.margin * 100).toFixed(1)}%
        </b>
      </div>
      <div className="ltv-fig">
        <span className="k">{t("Average job")}</span>
        <b>{formatMoney(value.averageJobSize)}</b>
      </div>
      {/* Counts are neither good nor bad news, so they stay black. */}
      <div className="ltv-fig">
        <span className="k">{t("Total jobs")}</span>
        <b className="plain">{value.totalJobs}</b>
      </div>
      <div className="ltv-fig">
        <span className="k">{t("Approved jobs")}</span>
        <b className="plain">{value.approvedJobs}</b>
      </div>
      <div className="ltv-fig">
        <span className="k">{t("Conversion")}</span>
        <b
          className={
            value.conversionRate < HEALTHY_CONVERSION_RATE ? "bad" : undefined
          }
        >
          {(value.conversionRate * 100).toFixed(0)}%
        </b>
      </div>
    </div>
  );
}

/**
 * Deleting a customer nobody has any work for.
 *
 * Keeps its confirmation: this is outside the undo that covers the order
 * editors and nothing puts it back. `delete_contact` refuses if any job is
 * still attached, so a mis-click cannot take work with it.
 */
function DeleteContactButton({
  contact,
  onDeleted,
}: {
  contact: Contact;
  onDeleted: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    if (
      !window.confirm(
        t("Delete {name}?", { name: contact.full_name ?? t("this customer") }) +
          "\n\n" +
          t(
            "They have no jobs, so nothing else goes with them. This cannot be undone.",
          ),
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const { error: rpcError } = await supabase.rpc("delete_contact", {
      p_contact: contact.id,
    });
    setBusy(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    await onDeleted();
    navigate("");
  };

  return (
    <div style={{ marginTop: 14 }}>
      {error && <ErrorNote error={error} />}
      <button
        className="addrow ghost"
        disabled={busy}
        onClick={() => void remove()}
      >
        {busy ? t("Deleting…") : t("Delete this customer")}
      </button>
      <div className="na-hint" style={{ marginTop: 4 }}>
        {t("They have no jobs. Useful for a duplicate entered twice.")}
      </div>
    </div>
  );
}
